import { isObject, uid } from "@dhx/ts-common/core";
import { create, el, VNode } from "@dhx/ts-common/dom";
import { eventHandler, isIE, locate } from "@dhx/ts-common/html";
import { IHandlers } from "@dhx/ts-common/types";
import { View } from "@dhx/ts-common/view";
import { CustomShape, flowShapes, shapes, locale } from "@dhx/ts-diagram";
import { Group } from "@dhx/ts-diagram/sources/components/Groups/Group";
import { GroupSwimlane } from "@dhx/ts-diagram/sources/components/Groups/GroupSwimlane";
import { Swimlane } from "@dhx/ts-diagram/sources/components/Swimlane/Swimlane";
import { getIcon } from "./configs/icons";

export class ShapesBar extends View {
	private _htmlEvents: IHandlers;
	private _shadow: VNode;
	private _dropdowns = [];
	private _shapes;
	private _defaults;

	private _pressedShapeInfo;
	private _pull = {};

	constructor(container: VNode, config: any) {
		super(container, config);

		this._shapes = this.config.diagram.flowShapes || {};
		this._defaults = this.config.defaults || {};

		this._htmlEvents = {
			onmousedown: eventHandler(event => locate(event), {
				dhx_shape_thumb: (event, item) => {
					if (event.which !== 1) {
						return;
					}

					this._pressedShapeInfo = {
						...this._pull[item],
						$x: event.x,
						$y: event.y,
					};
					document.addEventListener("mousemove", this._handleMove);
					document.addEventListener("mouseup", this._handleUp);
				},
			}),
		};
		const view = create({ render: vm => this._render(vm) }, this);
		this.mount(container, view);
	}

	private _handleMove = event => {
		if (
			Math.abs(event.pageX - this._pressedShapeInfo.$x + event.pageY - this._pressedShapeInfo.$y) < 10
		) {
			if (!this._shadow) {
				return;
			}
		}
		if (!this._pressedShapeInfo) {
			return;
		}
		if (!this._shadow) {
			const render = () => {
				return this._getShadow();
			};
			this._shadow = create({ render }, this);
			this._shadow.mount(document.body);
		}
		this._pressedShapeInfo.$x = event.clientX;
		this._pressedShapeInfo.$y = event.clientY;
		this._shadow.redraw();
	};

	private _handleUp = () => {
		document.removeEventListener("mousemove", this._handleMove);
		document.removeEventListener("mouseup", this._handleUp);
		this.config.events.fire("shapesUp", [{ ...this._pressedShapeInfo }]);

		this._pressedShapeInfo = null;
		if (this._shadow) {
			this._shadow.unmount();
			this._shadow = null;
		}
	};
	private _toggle = label => {
		this._dropdowns[label] = !this._dropdowns[label];
	};

	private _getTextIcon() {
		return getIcon("text-shape", "", 60, 48);
	}

	private _wrapDropdown(label: string, content: any, open = true) {
		const id = label;
		this._dropdowns[label] = !this._dropdowns[label] ? open : this._dropdowns[label];

		return el(".dhx_diagram_editor__dropdown", [
			el("input", {
				class: "dhx_diagram_editor__dropdown_checkbox",
				id,
				name: id,
				type: "checkbox",
				checked: this._dropdowns[label],
				onchange: [this._toggle, label],
			}),
			el("label", { class: "dhx_diagram_editor__dropdown_label", for: id }, label),
			el(".dhx_diagram_editor__dropdown_content", [content]),
		]);
	}

	private _getShadow() {
		if (!this._pressedShapeInfo) {
			return this._shadow;
		}

		const item = JSON.parse(JSON.stringify(this._pressedShapeInfo));
		const shape = shapes[item.type];
		let shadowNode;

		switch (item.type) {
			case "$swimlane":
				shadowNode = this.getSwimlaneNode(
					{
						...item,
						width: item.width * 2,
						height: item.height * 2,
					},
					{
						data: this.config.diagram.data,
					}
				);
				break;
			case "$group":
				shadowNode = this.getGroupNode(item, {
					data: this.config.diagram.data,
				});
				break;
			case "text":
				shadowNode = this._getTextIcon();
				break;

			default:
				shadowNode = shape
					? new shape(item, {
							defaults: this._defaults[item.type] || {},
							shapes: this._shapes,
					  }).render()
					: new CustomShape(item, {
							defaults: this._defaults[item.type] || {},
							shapes: this._shapes,
					  }).render();
				break;
		}

		return el(
			".dhx_shape_shadow",
			{
				_key: item.$key,
				style: {
					top: item.$y,
					left: item.$x,
				},
			},
			[shadowNode]
		);
	}

	private _getWrapped(type: string, item, custom?: any): VNode {
		const shape = shapes[type];
		const preview = this.config.defaults[type]?.preview || {};

		let { margin, scale } = this.config;
		let defWidth;
		let defHeight;
		let defScale;
		let defMargin;
		let defImage;

		if (this.config.defaults[type] && typeof preview === "string") {
			defImage = preview;
		} else {
			if (preview.img) {
				defImage = preview.img;
			}
			if (preview.gap) {
				defMargin = preview.gap;
			}
			if (preview.scale) {
				defScale = preview.scale;
			}
			if (preview.width) {
				defWidth = preview.width;
			}
			if (preview.height) {
				defHeight = preview.height;
			}
		}
		scale = defScale || scale;
		margin = defMargin || margin;

		if (defImage) {
			return el(
				".dhx_shape_thumb.dhx_diagram_editor_shapebar__thumb_container",
				{
					_key: item.$key,
					dhx_id: item.$key,
					style: {
						width: defWidth || "calc(100%/3)",
						height: defHeight || "100%",
						margin,
					},
				},
				[
					el("img", {
						style: {
							maxWidth: "100%",
							maxHeight: "100%",
						},
						src: defImage || "",
						alt: type,
					}),
					el("div.dhx_shape_thumb--img-overlay"),
				]
			);
		}

		let widthContainer;
		let heightContainer;
		const { width, height } = item;

		const isBaseType = Object.keys(flowShapes).includes(type);
		const isOrgType = ["img-card", "card", "topic"].includes(type);

		if (isBaseType) {
			widthContainer = this.config.defaults[type]?.width * scale || 140 * scale;
			heightContainer = this.config.defaults[type]?.height * scale || 90 * scale;
		} else {
			widthContainer = width * scale;
			heightContainer = height * scale;
		}

		let itemNode;

		switch (type) {
			case "$group":
				itemNode = this.getGroupNode(
					{
						...item,
						type,
						width,
						height,
					},
					{
						data: this.config.diagram.data,
					}
				);
				break;
			case "$swimlane":
				itemNode = this.getSwimlaneNode(
					{
						...item,
						type,
						width,
						height,
					},
					{
						data: this.config.diagram.data,
					}
				);
				break;
			default:
				if (isBaseType) {
					item.text = item.text || item.type[0].toUpperCase() + item.type.slice(1);
				}
				if (isOrgType) {
					item.text = item.text || "Text";
					item.title = item.title || "Title";
				}
				if (type === "topic") {
					item.height = this.config.defaults[type]?.height || 30;
				}

				itemNode = shape
					? new shape(item, {
							defaults: this._defaults[type] || {},
							shapes: this._shapes,
					  }).render()
					: new CustomShape(item, {
							defaults: this._defaults[type] || {},
							shapes: this._shapes,
					  }).render();
				break;
		}

		return el(
			".dhx_shape_thumb.dhx_diagram_editor_shapebar__thumb_container",
			{
				_key: item.$key,
				dhx_id: item.$key,
				style: {
					width: widthContainer,
					height: heightContainer,
					margin,
				},
			},
			[
				custom
					? custom
					: el(
							"div",
							{
								style: {
									transform: `scale(${scale})`,
									transformOrigin: isIE() ? "0" : "50%",
								},
							},
							[itemNode]
					  ),
			]
		);
	}

	private getGroupNode(item, params): VNode {
		return new Group(item, params).render();
	}

	private getSwimlaneNode(item, params): VNode {
		const swimlaneCells = {};
		const layout = item.layout;

		if (item.layout.length) {
			const collection = new Set(layout.flat(Infinity));
			item.groupChildren = Array.from(collection);

			for (let i = 0; i < layout.length; i++) {
				const row = layout[i];
				for (let k = 0; k < row.length; k++) {
					const cell = layout[i][k];
					if (!swimlaneCells[cell]) {
						swimlaneCells[cell] = {
							type: "$sgroup",
							id: cell,
							groupChildren: [],
						};

						swimlaneCells[cell].$item = new GroupSwimlane(swimlaneCells[cell], params);
					}
				}
			}
		}

		return new Swimlane(item, params).render(Object.values(swimlaneCells));
	}

	private _shapeInit(item): VNode {
		let icon;
		const isCircle = ["circle", "junction", "or"].includes(item.type);

		item.width = item.width || this.config.defaults[item.type]?.width || (isCircle ? 90 : 140);
		item.height = item.height || this.config.defaults[item.type]?.height || 90;

		if (item.type === "text") {
			item.text = "Text";
			icon = this._getTextIcon();
		}
		if (item.type === "topic") {
			return this._getWrapped(item.type, { ...item, height: 90 });
		}
		return this._getWrapped(item.type, item, icon);
	}

	private _barCreator(): VNode[] {
		const node: VNode[] = [];
		let count = 0;

		const addToPull = (count: number, value: any): void => {
			this._pull[count] = {
				$key: count,
				type: value,
			};
			if (isObject(value)) {
				this._pull[count] = { ...this._pull[count], ...value };
			}
		};

		if (this.config.shapeSections) {
			for (const section in this.config.shapeSections) {
				const shapesNode = [];

				this.config.shapeSections[section].forEach(value => {
					if (value === true) {
						Object.keys(flowShapes).forEach(type => {
							++count;
							addToPull(count, type);
							shapesNode.push(this._shapeInit(this._pull[count]));
						});
					} else {
						++count;
						addToPull(count, value);
						shapesNode.push(this._shapeInit(this._pull[count]));
					}
				});
				node.push(this._wrapDropdown(section, el(".dhx_diagram_editor_shapebar", shapesNode)));
			}
		} else {
			const group = { type: "$group", header: { enable: false }, height: 300, width: 220 };
			const groupWithHeader = {
				type: "$group",
				header: { text: "Group", closable: true },
				height: 300,
				width: 220,
			};

			const rowSwimlane = {
				type: "$swimlane",
				height: 340,
				width: 480,
				header: { text: "Swimlane rows", closable: true },
				layout: [[uid()], [uid()], [uid()]],
				subHeaderRows: { enable: true },
				subHeaderCols: { enable: true },
			};
			const colSwimlane = {
				type: "$swimlane",
				height: 340,
				width: 480,
				header: { text: "Swimlane cols", closable: true },
				layout: [[uid(), uid(), uid()]],
				subHeaderRows: { enable: true },
				subHeaderCols: { enable: true },
			};
			const mixSwimlane = {
				type: "$swimlane",
				height: 340,
				width: 480,
				header: { text: "Swimlane mix", closable: true },
				layout: [
					[uid(), uid(), uid()],
					[uid(), uid(), uid()],
					[uid(), uid(), uid()],
				],
				subHeaderRows: { enable: true },
				subHeaderCols: { enable: true },
			};

			const shapesNode = Object.keys(this._defaults).map(type => {
				++count;
				addToPull(count, type);
				return this._shapeInit(this._pull[count]);
			});
			const groupNode = [group, groupWithHeader].map(type => {
				++count;
				addToPull(count, type);
				return this._shapeInit(this._pull[count]);
			});
			const swimlaneNode = [rowSwimlane, colSwimlane, mixSwimlane].map(type => {
				++count;
				addToPull(count, type);
				return this._shapeInit(this._pull[count]);
			});

			node.push(
				this._wrapDropdown(locale.shapeSections, el(".dhx_diagram_editor_shapebar", shapesNode))
			);
			node.push(
				this._wrapDropdown(locale.groupSections, el(".dhx_diagram_editor_shapebar", groupNode))
			);
			node.push(
				this._wrapDropdown(locale.swimlaneSections, el(".dhx_diagram_editor_shapebar", swimlaneNode))
			);
		}

		return node;
	}

	private _render(vm) {
		return el(
			".dhx_diagram_editor_shapebar__wrapper",
			{
				onclick: this._htmlEvents.onclick,
				onmousedown: this._htmlEvents.onmousedown,
			},
			[...this._barCreator()]
		);
	}
}
